import React from 'react';
import { Loader2, Upload } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { useI18n } from '../contexts/I18nContext';

/**
 * Modal for importing shipment changes from a previously-exported Excel file.
 * Stateless — owner controls dialog open/close and file handling.
 */
export function ExcelImportDialog({ open, onOpenChange, fileInputRef, onFileChange, importingExcel }) {
  const { t } = useI18n();
  return (
    <Dialog open={open} onOpenChange={(o) => !importingExcel && onOpenChange(o)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('shipments.importFromExcel')}</DialogTitle>
          <DialogDescription>
            {t('shipments.importExcelDescription')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="bg-muted/50 p-4 rounded-lg space-y-3">
            <p className="text-sm font-medium">{t('shipments.instructions')}</p>
            <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
              <li>{t('shipments.bulkImportInstructions1')}</li>
              <li>{t('shipments.bulkImportInstructions2')}</li>
              <li>{t('shipments.bulkImportInstructions3')}</li>
            </ol>
          </div>

          <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={onFileChange}
              className="hidden"
              id="excel-upload"
              data-testid="excel-file-input"
            />
            <label htmlFor="excel-upload" className="cursor-pointer flex flex-col items-center gap-2">
              <Upload className="h-10 w-10 text-muted-foreground" />
              <span className="text-sm font-medium">{t('shipments.clickToSelectFile')}</span>
              <span className="text-xs text-muted-foreground">{t('shipments.onlyXlsx')}</span>
            </label>
          </div>

          {importingExcel && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('shipments.processingFile')}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={importingExcel}>
            {t('shipments.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
